// React hook that surfaces the role-filtered + bucket-filtered dock
// sections for the current user. Memoized on the auth / capability /
// bucket inputs so iterating the result in `ActivityDockExpandedPanel`'s
// render does not re-filter on every render.
//
// L2 of docs/planning/firefly_role_context_presentation.md (closes the
// §7 L2 leak): the optional `currentBucket` arg layers
// `bucketRelevance` filtering on top of the role / capability gate.
// Defaults to `null` (fail-open) so any non-bucket-aware caller keeps
// pre-L2 behavior; the production caller (`ActivityDockExpandedPanel`)
// derives `currentBucket` once at the dock-mount level via
// `getBucketForMode(currentMode)` and threads it in.

import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";
import type { DockSectionDefinition } from "./sectionRegistry";
import { getDockSectionsForAuth } from "./sectionGate";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";

export function useActivityDockSections(
  currentBucket: BucketId | null = null,
): readonly DockSectionDefinition[] {
  const auth = useAuth();
  const hasCapability = useUserCapabilities();
  return useMemo(
    () =>
      getDockSectionsForAuth(
        (...roles: string[]) => auth.hasRole(...roles),
        hasCapability,
        currentBucket,
      ),
    [auth, hasCapability, currentBucket],
  );
}
